import React, { useEffect, useId, useRef, useState } from 'react'
import { Box, Typography, useTheme, type SxProps, type Theme, type TypographyProps } from '@mui/material'
import GenericButton from '../GenericButton'
import { ButtonSize } from '../GenericButton/styles'
import { blue } from '../../colors'

interface ExpandableTextProps {
  children: React.ReactNode
  /** Number of rows shown while collapsed. */
  collapsedLineCount?: number
  variant?: TypographyProps['variant']
  sx?: SxProps<Theme>
  showMoreLabel?: string
  showLessLabel?: string
  'aria-label'?: string
}

/**
 * Clamps long text to `collapsedLineCount` rows and reveals the rest behind a
 * Show more / Show less toggle. The toggle is only rendered when the text
 * actually overflows the clamp, so short content renders exactly as plain text.
 */
const ExpandableText: React.FC<ExpandableTextProps> = ({
  children,
  collapsedLineCount = 1,
  variant = 'body1',
  sx = {},
  showMoreLabel = 'Show more',
  showLessLabel = 'Show less',
  'aria-label': ariaLabel
}) => {
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)
  const textId = useId()

  // Only measured while collapsed: expanding drops the clamp, so an expanded element
  // never overflows and re-measuring there would make the Show less button vanish.
  useEffect(() => {
    if (isExpanded) return
    const element = textRef.current
    if (element == null) return

    const measure = (): void => {
      setIsOverflowing(element.scrollHeight - element.clientHeight > 1)
    }
    measure()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => { measure() })
        : null
    resizeObserver?.observe(element)

    return () => {
      resizeObserver?.disconnect()
    }
  }, [children, collapsedLineCount, isExpanded])

  return (
    <Box display="flex" alignItems="flex-end" gap={theme.spacing(2)}>
      <Typography
        ref={textRef}
        id={textId}
        variant={variant}
        aria-label={ariaLabel}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          ...(isExpanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: collapsedLineCount,
                overflow: 'hidden'
              }),
          ...sx
        }}
      >
        {children}
      </Typography>
      {isOverflowing && (
        <GenericButton
          aria-expanded={isExpanded}
          aria-controls={textId}
          buttonSize={ButtonSize.Small}
          label={isExpanded ? showLessLabel : showMoreLabel}
          onClick={() => { setIsExpanded((wasExpanded) => !wasExpanded) }}
          // MUI's ButtonBase zeroes `outline`, which also disables the app-wide
          // `*:focus-visible` ring, so this text-only toggle draws its own.
          sx={{ '&.Mui-focusVisible': { outline: `2px solid ${blue[500]}`, outlineOffset: '2px' } }}
          style={{
            padding: 0,
            minWidth: 'auto',
            // The shared Small button is 36px tall; keep the toggle text-height so a
            // collapsed line stays one row tall.
            height: 'auto',
            minHeight: 0,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            textDecoration: 'underline',
            textUnderlineOffset: theme.spacing(0.5)
          }}
        />
      )}
    </Box>
  )
}

export default ExpandableText
